import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { 
  AppWindow, 
  Users, 
  Bell, 
  CheckCircle, 
  TrendingUp,
  Activity 
} from 'lucide-react';

export function DashboardPage() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.getDashboardStats(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const { data: queueStats } = useQuery({
    queryKey: ['queue-stats'],
    queryFn: () => api.getQueueStats(),
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const statsData = stats?.data;
  const queues = queueStats?.data;

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          title="Total Applications"
          value={statsData?.totalApplications || 0}
          icon={AppWindow}
          color="blue"
        />
        <StatCard
          title="Active Subscriptions"
          value={statsData?.activeSubscriptions || 0}
          icon={Users}
          color="green"
        />
        <StatCard
          title="Total Notifications"
          value={statsData?.totalNotifications || 0}
          icon={Bell}
          color="purple"
        />
        <StatCard
          title="Notifications Today"
          value={statsData?.notificationsToday || 0}
          icon={Activity}
          color="yellow"
        />
        <StatCard
          title="Delivery Rate"
          value={`${(statsData?.deliveryRate || 0).toFixed(1)}%`}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          title="Total Subscriptions"
          value={statsData?.totalSubscriptions || 0}
          icon={TrendingUp}
          color="blue"
        />
      </div>

      {/* Queue Status */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">Queue Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <QueueCard
            name="Transactional"
            stats={queues?.transactional}
            color="blue"
          />
          <QueueCard
            name="Marketing"
            stats={queues?.marketing}
            color="purple"
          />
          <QueueCard
            name="Cleanup"
            stats={queues?.cleanup}
            color="yellow"
          />
        </div>
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  color: 'blue' | 'green' | 'purple' | 'yellow' | 'red';
}

function StatCard({ title, value, icon: Icon, color }: StatCardProps) {
  const colors = {
    blue: 'bg-blue-500/10 text-blue-500',
    green: 'bg-green-500/10 text-green-500',
    purple: 'bg-purple-500/10 text-purple-500',
    yellow: 'bg-yellow-500/10 text-yellow-500',
    red: 'bg-red-500/10 text-red-500',
  };

  return (
    <div className="card">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${colors[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-sm text-slate-400">{title}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

interface QueueCardProps {
  name: string;
  stats?: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
  };
  color: 'blue' | 'purple' | 'yellow';
}

function QueueCard({ name, stats, color }: QueueCardProps) {
  const colors = {
    blue: 'border-blue-500',
    purple: 'border-purple-500',
    yellow: 'border-yellow-500',
  };

  return (
    <div className={`bg-slate-700/50 rounded-lg p-4 border-l-4 ${colors[color]}`}>
      <h4 className="font-medium text-white mb-3">{name}</h4>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-slate-400">Waiting:</span>
          <span className="ml-2 text-white">{stats?.waiting || 0}</span>
        </div>
        <div>
          <span className="text-slate-400">Active:</span>
          <span className="ml-2 text-white">{stats?.active || 0}</span>
        </div>
        <div>
          <span className="text-slate-400">Completed:</span>
          <span className="ml-2 text-green-400">{stats?.completed || 0}</span>
        </div>
        <div>
          <span className="text-slate-400">Failed:</span>
          <span className="ml-2 text-red-400">{stats?.failed || 0}</span>
        </div>
      </div>
    </div>
  );
}
